import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors, FieldProps, withFieldWrap } from '@/components/forms/common/FormBase'
import { Control, Controller, FieldValues, Path, useForm } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { useSession } from '@/hooks/useSession'
import { Translates } from '@/services/repository/i18n'
import { Spinner } from '@/components/spinner'
import { SimpleMachine } from '@/pages/for/superadmin/machines/Machines'
import { Roles, isAdminRole } from '@/services/repository/users'

// export interface SimpleMachine {
//   id: string
//   email: string
//   machinename: string
//   createdAt: Date
// }

export interface MachineRow {
  id: string
  machinename: string
}

export const SearchMachinesPopup = ({ services, session, onClose, onSelect }: {
  services: Services,
  session: CurrentSession,
  onClose: () => void,
  onSelect: (machine: SimpleMachine) => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  if (!session || !services) {
    return <Spinner />
  }

  // const tableParams: SearchTableParams<SimpleMachine> = {
  //   fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
  //     return services.session.fetchWithToken(`/api/superadmin/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
  //       if (r.status === 200) {
  //         return r.json()
  //       }
  //       return Promise.reject()
  //     })
  //   },
  //   pageQuery: {
  //     pageNumber: 1,
  //     pageSize: 30,
  //     search: ""
  //   },
  //   header: () => <>
  //     <div className='hcol desktop-only'>{session.translates.for('elements.table.machines.createdAt')}</div>
  //     <div className='hcol desktop-only'>{session.translates.for('elements.table.machines.email')}</div>
  //     <div className='hcol'>{session.translates.for('elements.table.machines.machinename')}</div>
  //     <div className='hcol'></div>
  //   </>,
  //   row: (row) => <>
  //     <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
  //     <div className='col desktop-only'>{row.email}</div>
  //     <div className='col'>{row.machinename}</div>
  //     <div className='col' onClick={() => {
  //       onClose()
  //       onSelect(row)
  //     }}>
  //       <i className='svgfont-next' />
  //     </div>
  //   </>,
  //   translates: session.translates
  // }

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      return services.session.fetchWithToken(`/api/superadmin/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.machines.manufacturer')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.model')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.number')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.name')}</div>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.manufacturer}</div>
      <div className='col'>{row.model}</div>
      <div className='col'>{row.internalNumber ? row.internalNumber : row.serialNumber}</div>
      <div className='col'>{row.name}</div>
      <div className='col icocol' onClick={() => {
        onClose()
        onSelect(row)
      }}>
        <i className='svgfont-next' />
      </div>
    </>,
    translates: session.translates
  }

  return <div>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </div>
}

export const CommonMachinenameInput = (props: {
  fieldName: string
  formName: string
  translates: Translates
  value: string
  setValue: (v: string) => void
  readOnly?: boolean
  session: CurrentSession
  services: Services
}) => {
  const { openModal } = useModal()
  if (props.session !== undefined && isAdminRole(props.session.user.role)) {
    return <div className='des'>
      <div className="code-text-fild">
        <input
          type="text"
          id={props.fieldName}
          value={props.value}
          name={props.fieldName}
          readOnly={props.readOnly}
          onChange={(e) => props.setValue(e.target.value)}
          placeholder={props.translates.for(`inputs.machine_pointer.placeholder`)}
        />
        <div className="toolbox"
          onClick={props.readOnly ? undefined : () => openModal((popupprops) => SearchMachinesPopup({
            ...popupprops, services: props.services, session: props.session, onSelect: (machine) => {
              props.setValue(machine.internalNumber || machine.serialNumber)
            }
          }))}>
          <div className="icon-button">
            <i className="svgfont-machine"></i>
          </div>
        </div>
      </div>
    </div>
  }
  return <input
    type='text'
    id={props.fieldName}
    readOnly={props.readOnly}
    value={props.value}
    name={props.fieldName}
    onChange={(e) => props.setValue(e.target.value)}
    placeholder={props.translates.for(`inputs.machine_pointer.placeholder`)}
  />
}

export const MachinenameInput = <T extends FieldValues>(props: FieldProps<T>) => {
  return <Controller
    name={props.fieldName}
    control={props.form.control}
    rules={{
      required: props.required ? props.translates.for(`forms.${props.formName}.errors.fields.${props.fieldName}.required`) : undefined,
    }}
    render={({
      field: { value, onChange },
    }) => {
      const session = useSession(props.services)
      if (!session) {
        return <Spinner />
      }
      return <CommonMachinenameInput
        fieldName={props.fieldName}
        formName={props.formName}
        translates={props.translates}
        value={value}
        setValue={onChange}
        readOnly={props.readOnly}
        session={session}
        services={props.services}
      />
    }}
  />
}

export const machinenameField = <T extends FieldValues>() => withFieldWrap<T>(MachinenameInput<T>)